import { motion } from 'framer-motion';
import { Check, Crown } from 'lucide-react';
import { useState } from 'react';
import { useSiteConfig } from '../../hooks/useSiteConfig';
import { openWhatsApp } from '../../utils/whatsapp';

export default function PricingSection() {
  const siteConfig = useSiteConfig();
  const { pricing, contact } = siteConfig;
  const [labels, setLabels] = useState<Record<string, string>>({});

  const handleCta = (planId: string, message: string) => {
    setLabels((prev) => ({ ...prev, [planId]: '⏳ 正在開啟 WhatsApp...' }));
    openWhatsApp(contact.whatsapp, message);
  };

  return (
    <section id="pricing" className="py-20 md:py-28 bg-deep-navy relative overflow-hidden">
      <div className="absolute inset-0 bg-circuit-grid opacity-40" />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            {pricing.sectionTitle}
          </h2>
          <p className="text-base md:text-lg text-gray-400 mb-3">{pricing.subtitle}</p>
          <p className="text-sm text-gray-500 max-w-2xl mx-auto">{pricing.intro}</p>
        </motion.div>

        <div className="grid md:grid-cols-3 gap-6 lg:gap-8">
          {pricing.plans.map((plan, index) => (
            <motion.div
              key={plan.id}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.12 }}
              className={`relative rounded-3xl p-8 flex flex-col ${
                plan.recommended
                  ? 'neon-card border-neon-gold/50 shadow-neon-gold md:-translate-y-4 md:scale-105'
                  : 'neon-card'
              }`}
              style={plan.recommended ? { borderColor: 'rgba(212,168,83,0.5)' } : undefined}
            >
              {plan.recommended && (
                <div className="absolute -top-4 left-1/2 -translate-x-1/2 flex items-center gap-1.5 px-4 py-1.5 rounded-full bg-gradient-to-r from-neon-gold to-neon-purple text-deep-navy text-xs font-bold whitespace-nowrap">
                  <Crown size={14} />
                  最多人選擇
                </div>
              )}

              <h3 className="text-xl font-bold text-white mb-1 mt-2">{plan.name}</h3>
              <p className="text-xs text-neon-gold font-medium mb-1">{plan.stage}</p>
              <p className="text-sm text-gray-500 mb-6">{plan.tagline}</p>

              <div className="mb-6">
                <span
                  className={`text-2xl md:text-3xl font-black ${
                    plan.recommended ? 'text-neon-gold' : 'text-neon-cyan'
                  }`}
                >
                  {plan.priceLabel}
                </span>
                <span className="text-gray-500 text-sm ml-1">{plan.unit}</span>
              </div>

              <ul className="space-y-3 mb-8 flex-1">
                {plan.features.map((feature, i) => (
                  <li key={i} className="flex items-start gap-2.5 text-sm text-gray-300">
                    <Check size={16} className={plan.recommended ? 'text-neon-gold flex-shrink-0 mt-0.5' : 'text-neon-cyan flex-shrink-0 mt-0.5'} />
                    <span>{feature}</span>
                  </li>
                ))}
              </ul>

              <button
                onClick={() => handleCta(plan.id, plan.whatsappMessage)}
                className={`w-full py-3.5 rounded-full font-bold text-sm transition-all duration-300 hover:brightness-110 hover:-translate-y-0.5 ${
                  plan.recommended
                    ? 'bg-gradient-to-r from-neon-gold to-neon-purple text-deep-navy'
                    : 'bg-white/5 border border-neon-cyan/30 text-neon-cyan hover:bg-neon-cyan/10'
                }`}
              >
                {labels[plan.id] || 'WhatsApp 免費報價'}
              </button>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
